import { IStyles } from '../../../commonUtils/styleUtils';
import { IconWrapper } from '../../elements/IconWrapper/Index';
import { ScoreProgressBar } from '../ScoreProgressBar';
import { SelectMenu } from '../SelectMenu';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { theme } from '../../../theme/themeProvider';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase, Typography, useMediaQuery } from '@mui/material';
import { GilProjectStatusEnum } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { Fragment, ReactNode } from 'react';
import { GIL, gilSize, gilTableText, gilText } from './gilTheme';
import { PDF_BLOCK_CLASS, PDF_HIDE_CLASS } from './reportPdf';
import {
  STATUS_LABEL,
  formatDurationBreakdown,
  toPercent,
} from './utils';

/** Theme primary (coding) and warning (video / activity book). */
export const CODING_COLOR = '#007CDC';
export const VIDEO_COLOR = '#F8C807';

const styles: IStyles = {
  card: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    borderRadius: pxToRem(10),
    boxShadow: `0px 0px ${pxToRem(10)} #0000001F`,
    padding: {
      xs: `${pxToRem(16)} ${pxToRem(14)}`,
      md: `${pxToRem(18)} ${pxToRem(22)}`,
      lg: `${pxTovW(20)} ${pxTovW(26)}`,
    },
  },
  cardHeading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: pxToRem(20),
    marginBottom: { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
  },
  actionPill: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(4), md: pxToRem(8), lg: pxTovW(8) },
    cursor: 'pointer',
    borderRadius: { xs: pxToRem(5), md: pxToRem(15), lg: pxTovW(15) },
    backgroundColor: '#158D29',
    padding: { xs: pxToRem(8), md: pxToRem(10), lg: pxTovW(10) },
    flexShrink: 0,
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(var(--summary-count), minmax(0, 1fr))',
    },
    rowGap: { xs: pxToRem(20), md: 0 },
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxToRem(15), lg: pxTovW(15) },
    paddingX: { xs: pxToRem(6), md: pxToRem(18), lg: pxTovW(20) },
    minWidth: 0,
  },
  sectionHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: pxToRem(10),
  },
  panel: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${GIL.border}`,
    borderRadius: gilSize(16, 12),
    padding: gilSize(24, 16),
  },
  statusTag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: gilSize(12, 12),
    padding: {
      xs: `${pxToRem(4)} ${pxToRem(10)}`,
      md: `${pxToRem(5)} ${pxToRem(12)}`,
      lg: `${pxTovW(5)} ${pxTovW(12)}`,
    },
  },
  legendDot: {
    width: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    height: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    borderRadius: '50%',
    flexShrink: 0,
  },
};

interface SummaryCardProps {
  title: string;
  action?: { label: string; iconName?: string; onClick: () => void };
  children: ReactNode;
}

/** "Overall Summary" card, as on the homework report. */
export const SummaryCard = ({ title, action, children }: SummaryCardProps) => {
  const itemCount = Array.isArray(children)
    ? children.filter(Boolean).length
    : 1;
  return (
    <Box sx={styles.card} className={PDF_BLOCK_CLASS}>
      <Box sx={styles.cardHeading}>
        <Typography variant="bodyText" fontWeight="bold">
          {title}
        </Typography>
        {action && (
          <Box
            sx={styles.actionPill}
            onClick={action.onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => event.key === 'Enter' && action.onClick()}
          >
            <IconWrapper
              name={action.iconName ?? 'reports'}
              size="md"
              parentFolder="icons"
              type="png"
            />
            <Typography
              variant="smallText"
              fontWeight="bold"
              sx={{ color: '#FFFFFF', textTransform: 'uppercase' }}
            >
              {action.label}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...styles.summaryRow,
          '--summary-count': itemCount,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface SummaryItemProps {
  iconName: string;
  value: string | number;
  label: string;
  /** Renders a divider on its left from md up. */
  divider?: boolean;
  footer?: ReactNode;
}

/** Icon, bold value and grey label - the app's overview figure. */
export const SummaryItem = ({
  iconName,
  value,
  label,
  divider,
  footer,
}: SummaryItemProps) => (
  <Box
    sx={{
      ...styles.summaryItem,
      borderLeft: divider ? { md: '1px solid #E0DFDE' } : undefined,
    }}
  >
    <IconWrapper
      name={iconName}
      size="large"
      parentFolder="icons"
      type="png"
      customSx={{
        height: { xs: pxToRem(25), lg: pxTovW(40) },
        width: { xs: pxToRem(25), lg: pxTovW(40) },
        mt: pxToRem(4),
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(5.5), md: pxToRem(7.5), lg: pxTovW(7.5) },
        minWidth: 0,
        flex: 1,
      }}
    >
      <Typography variant="h2" fontWeight="bold" noWrap>
        {value}
      </Typography>
      <Typography variant="h4" fontWeight="regular" sx={{ color: '#828282' }}>
        {label}
      </Typography>
      {footer}
    </Box>
  </Box>
);

interface SectionCardProps {
  /** Left out for a section introduced by its strapline alone. */
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  /** Drops the white card, for content that brings its own container. */
  plain?: boolean;
  children: ReactNode;
}

/** Section title and grey strapline, with an optional control on the right. */
const SectionHeading = ({
  title,
  subtitle,
  action,
}: {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
}) => (
  <Box sx={styles.sectionHeader}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: gilSize(4, 2),
        minWidth: 0,
      }}
    >
      {title && (
        <Typography sx={gilText(24, 600, GIL.heading)}>{title}</Typography>
      )}
      {subtitle && (
        <Typography sx={gilText(16, 600, GIL.strap)}>{subtitle}</Typography>
      )}
    </Box>
    {action}
  </Box>
);

/** A titled section; the heading sits inside the white card unless `plain`. */
export const SectionCard = ({
  title,
  subtitle,
  action,
  plain,
  children,
}: SectionCardProps) =>
  plain ? (
    <Box className={PDF_BLOCK_CLASS}>
      <Box sx={{ paddingBottom: gilSize(20, 12) }}>
        <SectionHeading title={title} subtitle={subtitle} action={action} />
      </Box>
      {children}
    </Box>
  ) : (
    <Box
      className={PDF_BLOCK_CLASS}
      sx={{
        ...styles.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: gilSize(20, 16),
      }}
    >
      <SectionHeading title={title} subtitle={subtitle} action={action} />
      {children}
    </Box>
  );

const STATUS_COLORS: Record<GilProjectStatusEnum, { fg: string; bg: string }> =
  {
    [GilProjectStatusEnum.GIL_PROJECT_STATUS_UNDEFINED]: {
      fg: '#5F6368',
      bg: '#F1F3F4',
    },
    [GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED]: {
      fg: '#5F6368',
      bg: '#F1F3F4',
    },
    [GilProjectStatusEnum.GIL_PROJECT_STATUS_IN_PROGRESS]: {
      fg: '#B06000',
      bg: '#FEF7E0',
    },
    [GilProjectStatusEnum.GIL_PROJECT_STATUS_COMPLETED]: {
      fg: '#16A34A',
      bg: '#DCFCE7',
    },
  };

export const StatusChip = ({ status }: { status?: GilProjectStatusEnum }) => {
  const value = status ?? GilProjectStatusEnum.GIL_PROJECT_STATUS_UNDEFINED;
  const colors = STATUS_COLORS[value];
  return (
    <Box
      component="span"
      sx={{ ...styles.statusTag, backgroundColor: colors.bg }}
    >
      <Typography noWrap sx={gilTableText(12, 500, colors.fg)}>
        {STATUS_LABEL[value]}
      </Typography>
    </Box>
  );
};

/**
 * Resources (Video / Activity Book) vs Coding as one stacked bar with a legend.
 * `compact` drops the legend for use inside table cells.
 */
export const TimeSplitBar = ({
  videoSec = 0,
  codingSec = 0,
  compact = false,
}: {
  videoSec?: number;
  codingSec?: number;
  compact?: boolean;
}) => {
  const total = videoSec + codingSec;
  const videoShare = toPercent(videoSec, total);
  const {
    parts: [videoText, codingText],
    total: totalText,
  } = formatDurationBreakdown([videoSec, codingSec]);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(12), md: pxToRem(16), lg: pxTovW(16) },
        minWidth: compact ? pxToRem(90) : undefined,
      }}
    >
      <Box
        role="img"
        aria-label={`Resources ${videoText}, Coding ${codingText}`}
        sx={{
          display: 'flex',
          height: compact
            ? { xs: pxToRem(8), md: pxToRem(10), lg: pxTovW(10) }
            : { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
          borderRadius: { xs: pxToRem(4), md: pxToRem(4), lg: pxTovW(4) },
          overflow: 'hidden',
          backgroundColor: '#E0DFDE',
        }}
      >
        {total > 0 && (
          <>
            <Box
              sx={{ width: `${videoShare}%`, backgroundColor: VIDEO_COLOR }}
            />
            <Box sx={{ flex: 1, backgroundColor: CODING_COLOR }} />
          </>
        )}
      </Box>
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: { xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) },
            rowGap: pxToRem(8),
          }}
        >
          <Legend color={VIDEO_COLOR} label="Resources" value={videoText} />
          <Legend color={CODING_COLOR} label="Coding" value={codingText} />
          <Legend label="Total" value={totalText} />
        </Box>
      )}
    </Box>
  );
};

const Legend = ({
  color,
  label,
  value,
}: {
  color?: string;
  label: string;
  value: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: pxToRem(6), md: pxToRem(8), lg: pxTovW(8) },
    }}
  >
    {color && <Box sx={{ ...styles.legendDot, backgroundColor: color }} />}
    <Typography variant="h4" fontWeight="regular" sx={{ color: '#828282' }}>
      {label}
    </Typography>
    <Typography variant="h4" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

/** Score as the app's red / amber / green progress bar, or "—" when absent. */
export const ScoreBar = ({ value }: { value?: number }) => {
  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  return value === undefined ? (
    <Typography sx={gilTableText(14, 400)}>—</Typography>
  ) : (
    <Box
      sx={{ width: { xs: pxToRem(110), md: pxToRem(102), lg: pxTovW(160) } }}
    >
      <ScoreProgressBar
        variant={largeScreen ? 'small' : 'md'}
        score={Math.round(value)}
      />
    </Box>
  );
};

/** Neutral progress: a thin track with the percentage beside it. */
export const PercentBar = ({
  value,
  color = GIL.completion,
  trackWidth = 120,
}: {
  value?: number;
  color?: string;
  /** Track length in Figma px. */
  trackWidth?: number;
}) => {
  if (value === undefined) {
    return <Typography sx={gilTableText(14, 400, GIL.cellSub)}>—</Typography>;
  }
  const percent = Math.min(100, Math.max(0, Math.round(value)));
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: gilSize(8, 8),
      }}
    >
      <Box
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{
          width: gilSize(trackWidth, Math.round(trackWidth * 0.8)),
          height: gilSize(8, 6),
          borderRadius: gilSize(4, 3),
          backgroundColor: GIL.track,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{ width: `${percent}%`, height: '100%', backgroundColor: color }}
        />
      </Box>
      <Typography sx={gilTableText(14, 500, percent ? GIL.cell : GIL.cellSub)}>
        {percent}%
      </Typography>
    </Box>
  );
};

/** Icon, badge and card tints of the overview tiles. */
export type StatTone = 'purple' | 'green' | 'blue' | 'yellow' | 'red';

const STAT_TONES: Record<StatTone, { fg: string; bg: string; badge: string }> =
  {
    green: { fg: '#11A465', bg: '#EFFBF6', badge: '#DBF6EA' },
    blue: { fg: '#3F4D8F', bg: '#F0F7FD', badge: '#DDECFE' },
    purple: {
      fg: '#A5247F',
      bg: 'rgba(165, 36, 127, 0.05)',
      badge: 'rgba(165, 36, 127, 0.15)',
    },
    red: { fg: '#FD1213', bg: '#FEF1F4', badge: '#FEE3E6' },
    yellow: { fg: '#FEB918', bg: '#FEF9EA', badge: '#FEF2D4' },
  };

interface OverviewSectionProps {
  /** Left out for a bare row of tiles under the page header. */
  title?: string;
  subtitle?: string;
  action?: { label: string; onClick: () => void };
  children: ReactNode;
}

/** Heading over a row of `StatTile`s - the "Group Overview" block. */
export const OverviewSection = ({
  title,
  subtitle,
  action,
  children,
}: OverviewSectionProps) => (
  <Box className={PDF_BLOCK_CLASS}>
    {(title || subtitle || action) && (
      <SectionHeading
        title={title}
        subtitle={subtitle}
        action={
          action && (
            <Typography
              role="button"
              tabIndex={0}
              onClick={action.onClick}
              onKeyDown={(event) => event.key === 'Enter' && action.onClick()}
              sx={{
                ...gilText(13, 600, GIL.green),
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {action.label}
            </Typography>
          )
        }
      />
    )}
    <Box
      sx={{
        display: 'grid',
        // One tile per row on phones, wrapping on tablets so labels stay
        // whole, and a single row of tiles on desktop.
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: `repeat(auto-fit, minmax(${pxToRem(200)}, 1fr))`,
          lg: 'none',
        },
        gridAutoFlow: { lg: 'column' },
        gridAutoColumns: { lg: 'minmax(0, 1fr)' },
        gap: gilSize(16, 10),
      }}
    >
      {children}
    </Box>
  </Box>
);

interface StatTileProps {
  /** Name of a PNG in assets/icons, or an icon element tinted by `tone`. */
  icon: string | ReactNode;
  tone: StatTone;
  value: string | number;
  label: string;
  caption?: string;
}

/** KPI card: tinted badge, big value, label and a soft caption. */
export const StatTile = ({
  icon,
  tone,
  value,
  label,
  caption,
}: StatTileProps) => {
  const colors = STAT_TONES[tone];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: gilSize(20, 10),
        minWidth: 0,
        minHeight: gilSize(110, 80),
        boxSizing: 'border-box',
        backgroundColor: colors.bg,
        borderRadius: gilSize(8, 8),
        boxShadow: '1px 1px 4px rgba(159, 159, 159, 0.25)',
        padding: {
          xs: pxToRem(12),
          md: `${pxToRem(14)} ${pxToRem(11)} ${pxToRem(14)} ${pxToRem(10)}`,
          lg: `${pxTovW(14)} ${pxTovW(11)} ${pxTovW(14)} ${pxTovW(10)}`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          width: gilSize(67, 40),
          height: gilSize(67, 40),
          borderRadius: gilSize(8, 8),
          backgroundColor: colors.badge,
          boxShadow: '0px 0px 0px 1px #FFFFFF',
          color: colors.fg,
          '& svg': { fontSize: gilSize(36, 22) },
        }}
      >
        {typeof icon === 'string' ? (
          <IconWrapper
            name={icon}
            type="png"
            parentFolder="icons"
            size="md"
            customSx={{
              width: gilSize(36, 22),
              height: gilSize(36, 22),
              objectFit: 'contain',
            }}
          />
        ) : (
          icon
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* A tile with a caption sets its figure a size down (Figma). */}
        <Typography noWrap sx={gilText(caption ? 28 : 32, 600, GIL.heading)}>
          {value}
        </Typography>
        <Typography noWrap sx={gilText(16, 600, GIL.label)}>
          {label}
        </Typography>
        {caption && (
          <Typography noWrap sx={gilText(12, 600, GIL.caption)}>
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** One-word read of an average score, for the overview caption. */
export const performanceLabel = (percent?: number) => {
  if (percent === undefined) return 'Not graded yet';
  if (percent >= 80) return 'Excellent performance';
  if (percent >= 60) return 'Good performance';
  if (percent >= 40) return 'Average performance';
  return 'Needs improvement';
};

export interface ReportTab {
  key: string;
  label: string;
  icon?: ReactNode;
}

/** Underlined tab strip that switches the sections of a report. */
export const ReportTabs = ({
  tabs,
  value,
  onChange,
}: {
  tabs: ReportTab[];
  value: string;
  onChange: (key: string) => void;
}) => (
  <Box
    role="tablist"
    sx={{
      display: 'flex',
      gap: gilSize(40, 16),
      borderBottom: `1px solid ${GIL.border}`,
      overflowX: 'auto',
    }}
  >
    {tabs.map((tab) => {
      const active = tab.key === value;
      return (
        <Box
          key={tab.key}
          role="tab"
          aria-selected={active}
          tabIndex={0}
          onClick={() => onChange(tab.key)}
          onKeyDown={(event) => event.key === 'Enter' && onChange(tab.key)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: gilSize(10, 6),
            flexShrink: 0,
            cursor: 'pointer',
            paddingX: gilSize(20, 6),
            paddingY: gilSize(18, 10),
            marginBottom: '-1px',
            color: active ? GIL.tab : GIL.muted,
            borderBottom: `2px solid ${active ? GIL.tab : 'transparent'}`,
            '& svg': { fontSize: gilSize(28, 18) },
          }}
        >
          {tab.icon}
          <Typography noWrap sx={gilText(16, 700, 'inherit')}>
            {tab.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/** Video / activity book slice over the rest of the ring. */
const DONUT_VIDEO = '#FBBF24';
const DONUT_REST = '#3F4D8F';
const DONUT_SIZE = 140;
const DONUT_STROKE = 18;

/** Ring of the video / coding split with the total in the middle. */
const TimeDonut = ({
  videoSec,
  codingSec,
}: {
  videoSec: number;
  codingSec: number;
}) => {
  const total = videoSec + codingSec;
  const {
    parts: [videoText, codingText],
    total: totalText,
  } = formatDurationBreakdown([videoSec, codingSec]);
  const radius = (DONUT_SIZE - DONUT_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const videoLength = total ? (videoSec / total) * circumference : 0;
  const ring = {
    cx: DONUT_SIZE / 2,
    cy: DONUT_SIZE / 2,
    r: radius,
    fill: 'none',
    strokeWidth: DONUT_STROKE,
  };
  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: gilSize(DONUT_SIZE, 120),
        height: gilSize(DONUT_SIZE, 120),
      }}
    >
      <svg
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        width="100%"
        height="100%"
        role="img"
        aria-label={`Resources ${videoText}, Coding ${codingText}`}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle {...ring} stroke={total ? DONUT_REST : GIL.track} />
        {videoLength > 0 && (
          <circle
            {...ring}
            stroke={DONUT_VIDEO}
            strokeDasharray={`${videoLength} ${circumference}`}
          />
        )}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2px',
          textAlign: 'center',
        }}
      >
        <Typography sx={gilText(14, 700)}>{totalText}</Typography>
        <Typography sx={gilText(11, 400, GIL.muted)}>Total Time</Typography>
      </Box>
    </Box>
  );
};

/**
 * "Time Spent" row: the donut and its breakdown on a card, beside an
 * encouragement card.
 */
export const TimeSpentCard = ({
  videoSec = 0,
  codingSec = 0,
  subtitle = 'Total learning time across all activities by Students',
}: // Read by the encouragement card, which is hidden for now.
// effortText = 'Students are putting consistent effort into their learning journey.',
{
  videoSec?: number;
  codingSec?: number;
  subtitle?: string;
  /** Line under "Great Effort!" once any time is logged (card hidden for now). */
  effortText?: string;
}) => {
  const {
    parts: [videoText, codingText],
    total: totalText,
  } = formatDurationBreakdown([videoSec, codingSec]);
  const rows = [
    { label: 'Resources', color: DONUT_VIDEO, value: videoText },
    { label: 'Coding', color: GIL.completion, value: codingText },
    { label: 'Total', color: DONUT_REST, value: totalText },
  ];
  return (
    <Box
      className={PDF_BLOCK_CLASS}
      sx={{
        display: 'flex',
        // The encouragement card is too narrow for its heading beside the
        // donut on a tablet, so it sits underneath until desktop.
        flexDirection: { xs: 'column', lg: 'row' },
        gap: gilSize(24, 16),
      }}
    >
      <Box
        sx={{
          ...styles.panel,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: gilSize(20, 16),
        }}
      >
        <SectionHeading title="Time Spent" subtitle={subtitle} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: pxToRem(20), md: pxToRem(40), lg: pxTovW(120) },
            paddingLeft: { md: pxToRem(24), lg: pxTovW(80) },
          }}
        >
          <TimeDonut videoSec={videoSec} codingSec={codingSec} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: gilSize(12, 10),
              flex: 1,
              width: { xs: '100%', md: 'auto' },
              maxWidth: { md: pxToRem(420), lg: pxTovW(540) },
            }}
          >
            {rows.map((row, index) => (
              <Fragment key={row.label}>
                {index > 0 && (
                  <Box sx={{ borderTop: `1px solid ${GIL.border}` }} />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: pxToRem(10),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: gilSize(8, 8),
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: gilSize(8, 8),
                        height: gilSize(8, 8),
                        borderRadius: '50%',
                        flexShrink: 0,
                        backgroundColor: row.color,
                      }}
                    />
                    <Typography noWrap sx={gilText(13, 500)}>
                      {row.label}
                    </Typography>
                  </Box>
                  <Typography
                    noWrap
                    sx={{ ...gilText(13, 600), minWidth: gilSize(80, 70) }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              </Fragment>
            ))}
          </Box>
        </Box>
      </Box>
      {/* Encouragement card ("Great Effort!" / "Ready to Begin") hidden for now.
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: gilSize(16, 10),
          boxSizing: 'border-box',
          width: { xs: '100%', lg: pxTovW(400) },
          flexShrink: 0,
          backgroundColor: '#FEFBF0',
          border: '1px solid #FEF3C7',
          borderRadius: gilSize(16, 12),
          padding: gilSize(24, 16),
        }}
      >
        <IconWrapper
          name="gil-star-orange"
          type="png"
          parentFolder="icons"
          size="md"
          customSx={{
            width: gilSize(48, 36),
            height: gilSize(48, 36),
            objectFit: 'contain',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: gilSize(6, 4),
            maxWidth: { md: pxToRem(480), lg: pxTovW(336) },
          }}
        >
          <Typography sx={gilText(36, 700, '#11A465')}>
            {total ? 'Great Effort!' : 'Ready to Begin'}
          </Typography>
          <Typography sx={gilText(16, 400, GIL.muted)}>
            {total
              ? effortText
              : 'Time will show here once work on the project starts.'}
          </Typography>
        </Box>
      </Box>
      */}
    </Box>
  );
};

/** Status filter value that keeps every row. */
export const ALL_STATUSES = 'all';

const STATUS_FILTERS = [
  { value: ALL_STATUSES, label: 'All Status' },
  ...[
    GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED,
    GilProjectStatusEnum.GIL_PROJECT_STATUS_IN_PROGRESS,
    GilProjectStatusEnum.GIL_PROJECT_STATUS_COMPLETED,
  ].map((status) => ({ value: String(status), label: STATUS_LABEL[status] })),
];

/** True when a project row passes the search text and the status filter. */
export const matchesReportFilters = (
  name: string,
  status: GilProjectStatusEnum | undefined,
  search: string,
  statusFilter: string
) => {
  const query = search.trim().toLowerCase();
  return (
    (statusFilter === ALL_STATUSES || String(status) === statusFilter) &&
    (!query || name.toLowerCase().includes(query))
  );
};

/** Size of the report screens' SelectMenu; the search box shares its height. */
const FILTER_WIDTH = { xs: pxToRem(152), md: pxToRem(160), lg: pxTovW(230) };
const FILTER_HEIGHT = { xs: pxToRem(40), md: pxToRem(40), lg: pxTovW(56) };

/** Wider than the dropdown so a project name fits while typing. */
const SEARCH_WIDTH = { xs: '100%', md: pxToRem(240), lg: pxTovW(320) };

/** Borderless slate search field beside the status dropdown. */
const filterSx = {
  ...gilText(12, 500),
  height: FILTER_HEIGHT,
  borderRadius: gilSize(6, 6),
  backgroundColor: GIL.surface,
  paddingX: gilSize(12, 10),
  '& input::placeholder': { color: GIL.soft, opacity: 1 },
};

/** "Search projects..." box and "All Status" dropdown of a project table. */
export const ReportFilters = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  placeholder = 'Search projects...',
}: {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  placeholder?: string;
}) => (
  <Box
    className={PDF_HIDE_CLASS}
    sx={{
      display: 'flex',
      // One row from md up; only the phone layout may stack the two controls.
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      alignItems: 'center',
      flexShrink: 0,
      gap: gilSize(12, 8),
      width: { xs: '100%', md: 'auto' },
    }}
  >
    <InputBase
      placeholder={placeholder}
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      inputProps={{ 'aria-label': placeholder }}
      startAdornment={
        <SearchIcon
          sx={{
            color: GIL.muted,
            fontSize: gilSize(16, 16),
            marginRight: gilSize(6, 6),
          }}
        />
      }
      sx={{ ...filterSx, width: SEARCH_WIDTH }}
    />
    {/* The same dropdown as the other report screens. */}
    <SelectMenu
      value={
        STATUS_FILTERS.find((option) => option.value === status)?.label ??
        STATUS_FILTERS[0].label
      }
      onChange={(label) =>
        onStatusChange(
          STATUS_FILTERS.find((option) => option.label === label)?.value ??
            ALL_STATUSES
        )
      }
      optionList={STATUS_FILTERS.map((option, index) => ({
        id: index,
        name: option.label,
      }))}
      rootStyles={{
        width: FILTER_WIDTH,
        height: FILTER_HEIGHT,
        background: GIL.surface,
      }}
    />
  </Box>
);
